import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/brand/BrandMark";
import { fetchPageIndex } from "@/api/cms";
import { useMarket } from "./MarketProvider";
import { useMarketLink } from "./links";
import { MarketName } from "./MarketSwitcher";
import { useV3 } from "./useV3";
import { fill } from "./strings";
import { Flag } from "./Icons";

/* The footer, from home-*-1440.html.
 *
 * THE LINKS ARE THE PAGES THAT EXIST. The design draws five -- About,
 * Contact, FAQ, Terms, Privacy -- with href="#", which is the design saying
 * "these are content pages" rather than "these five exist". They are
 * `content_pages` rows, and this renders one link per PUBLISHED row plus
 * Privacy, which is a real built route.
 *
 * So today the footer is shorter than the design's. That is the honest state:
 * four of those pages have not been written. Each appears here the moment an
 * admin publishes it in Admin -> Content pages, with no deploy. Logged in
 * docs/DEVIATIONS.md.
 *
 * THE MARKET LINKS are every active market, from the rows. The active one is
 * bold and carries aria-current, as the design draws it.
 */
export function FooterV3() {
  const { s, locale } = useV3();
  const { market, markets } = useMarket();
  const link = useMarketLink();
  const [pages, setPages] = useState<{ slug: string; title: string }[]>([]);

  useEffect(() => {
    if (!market) return;
    const ctrl = new AbortController();
    let live = true;
    fetchPageIndex(locale, market.slug, ctrl.signal)
      .then((rows) => { if (live) setPages(rows); });
    return () => { live = false; ctrl.abort(); };
  }, [market, locale]);

  /* Privacy is a built route, not a content page, so it is always here and is
     not waiting on anyone to write it. If a content page ever takes the slug
     "privacy" the built route still wins -- one door, one destination. */
  const cms = pages.filter((p) => p.slug !== "privacy");

  return (
    <footer data-component="footer" className="sq-footer">
      <Link to={link("/")} className="sq-logo sq-logo--mobile" aria-label="SouqAkar">
        <BrandMark width={69} title="SouqAkar" />
      </Link>
      <nav className="sq-footer__links" aria-label={s.footer.label}>
        {cms.map((p) => (
          <Link key={p.slug} to={link(`/p/${p.slug}`)}>{p.title}</Link>
        ))}
        <Link to={link("/privacy")}>{s.footer.privacy}</Link>
      </nav>
      {/* The design's own inline style for this column, kept verbatim rather
          than renamed into a class that would then need documenting twice. */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
        {markets.length > 1 && (
          <div data-component="market-links" className="sq-footer__markets">
            <span>{s.footer.markets}</span>
            {markets.map((m) => {
              const active = m.id === market?.id;
              return (
                <Link key={m.id} to={`/${m.slug}/`}
                  aria-current={active ? "true" : undefined}
                  style={active
                    ? { fontWeight: 600 }
                    : { color: "var(--sq-color-text-secondary)" }}>
                  <Flag code={m.code} />
                  <MarketName market={m} />
                  {m.visibility === "coming_soon" && <small className="sq-market-tag">{s.marketComingSoon.tag}</small>}
                </Link>
              );
            })}
          </div>
        )}
        {/* The year is the build's, not a literal: a footer that says 2026
            forever is a site nobody is maintaining, in one number. */}
        <span>{fill(s.footer.copyright, { year: String(new Date().getFullYear()) })}</span>
      </div>
    </footer>
  );
}
